import { ThemedText } from "@/components/ThemedText";
import { getUserDisplayLabel } from "@/utils/getUserDisplayLabel";
import React from "react";
import { useTranslation } from "react-i18next";
import type { StyleProp, TextStyle } from "react-native";

type UserLike =
  | {
      displayName?: string | null;
      username?: string | null;
    }
  | null
  | undefined;

type Props = {
  user: UserLike;
  style?: StyleProp<TextStyle>;
  type?: React.ComponentProps<typeof ThemedText>["type"];
  numberOfLines?: number;
  ellipsizeMode?: "head" | "middle" | "tail" | "clip";
  loading?: boolean;
  loadingPlaceholder?: string;
  fallbackToUsername?: boolean;
  fallbackStyle?: StyleProp<TextStyle>;
};

const DEFAULT_FALLBACK_STYLE = { fontStyle: "italic" } as const;

export function DisplayName({
  user,
  style,
  type,
  numberOfLines,
  ellipsizeMode,
  loading = false,
  loadingPlaceholder = "...",
  fallbackToUsername = true,
  fallbackStyle = DEFAULT_FALLBACK_STYLE,
}: Props) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <ThemedText
        style={style}
        type={type}
        numberOfLines={numberOfLines}
        ellipsizeMode={ellipsizeMode}
      >
        {loadingPlaceholder}
      </ThemedText>
    );
  }

  if (!fallbackToUsername) {
    const trimmedDisplay = user?.displayName?.trim();
    if (trimmedDisplay) {
      return (
        <ThemedText
          style={style}
          type={type}
          numberOfLines={numberOfLines}
          ellipsizeMode={ellipsizeMode}
        >
          {trimmedDisplay}
        </ThemedText>
      );
    }
    return (
      <ThemedText
        style={[style, fallbackStyle]}
        type={type}
        numberOfLines={numberOfLines}
        ellipsizeMode={ellipsizeMode}
      >
        {t("ANONYMOUS_USER")}
      </ThemedText>
    );
  }

  const { label, isFallback } = getUserDisplayLabel(user, t);
  return (
    <ThemedText
      style={[style, isFallback && fallbackStyle]}
      type={type}
      numberOfLines={numberOfLines}
      ellipsizeMode={ellipsizeMode}
    >
      {label}
    </ThemedText>
  );
}
